import { useQuery } from "@tanstack/react-query";
import { ProductService } from "../services/productService";
import {
  InventoryStats,
  readInventoryStatsCache,
  writeInventoryStatsCache,
} from "../utils/inventoryStats";

// Keep stats fresh for 5 minutes on the client. Within this window React Query
// treats the localStorage-hydrated value as fresh and will NOT re-fetch. The
// backend also caches the aggregation in Redis for 5 minutes, so even a refetch
// rarely hits the database.
const STATS_TTL = 5 * 60 * 1000;

export const useInventoryStats = () => {
  // Read the persisted cache once so we can hydrate immediately on cold load.
  const cached = readInventoryStatsCache();

  const { data, isLoading, error, refetch, isFetching } = useQuery<InventoryStats>({
    queryKey: ["inventory-stats"],
    queryFn: async () => {
      const stats = await ProductService.getInventoryStats();
      writeInventoryStatsCache(stats);
      return stats;
    },
    staleTime: STATS_TTL,
    gcTime: STATS_TTL,
    // Hydrate from localStorage. If the cached value is younger than staleTime,
    // React Query considers it fresh and skips the network call entirely.
    initialData: cached?.stats,
    initialDataUpdatedAt: cached?.ts,
    refetchOnWindowFocus: false,
  });

  return { stats: data, isLoading, isFetching, error, refetch };
};
